import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save, Copy } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Form.jsx';
import AddressInput from '../../components/AddressInput.jsx';

// Free-text customer_type with a short list of datalist suggestions.
// Surfaced here so the form + list filter agree on common buckets without
// forcing users into an enum — they can type anything novel.
const CUSTOMER_TYPE_SUGGESTIONS = [
    'Individual',
    'Corporate',
    'Chauffeur partner',
    'Hotel',
    'Travel agency',
    'Event organiser',
];

// Address sub-schema. All fields optional — dispatcher can leave the
// whole block empty (we capture what we have). When the dispatcher does
// fill in an address, AddressInput supplies a normalised addressObj which
// hydrates these fields together; partial state is tolerated.
const addressSubSchema = z.object({
    address:                 z.string().trim().max(500).nullable().optional().or(z.literal('')),
    address_postcode:        z.string().trim().max(20).nullable().optional().or(z.literal('')),
    address_latitude:        z.union([z.number(), z.null(), z.string().length(0)]).optional(),
    address_longitude:       z.union([z.number(), z.null(), z.string().length(0)]).optional(),
    address_google_place_id: z.string().trim().max(300).nullable().optional().or(z.literal('')),
    address_source:          z.enum(['google_places', 'manual']).nullable().optional(),
});

const billingAddressSubSchema = z.object({
    billing_address_differs:         z.boolean().optional(),
    billing_address:                 z.string().trim().max(500).nullable().optional().or(z.literal('')),
    billing_address_postcode:        z.string().trim().max(20).nullable().optional().or(z.literal('')),
    billing_address_latitude:        z.union([z.number(), z.null(), z.string().length(0)]).optional(),
    billing_address_longitude:       z.union([z.number(), z.null(), z.string().length(0)]).optional(),
    billing_address_google_place_id: z.string().trim().max(300).nullable().optional().or(z.literal('')),
    billing_address_source:          z.enum(['google_places', 'manual']).nullable().optional(),
});

const schema = z.object({
    first_name:             z.string().trim().min(1, 'First name is required').max(100),
    last_name:              z.string().trim().min(1, 'Last name is required').max(100),
    email:                  z.string().trim().min(1, 'Email is required').email('Enter a valid email').max(200),
    phone:                  z.string().trim().min(1, 'Phone is required').max(50),
    company_name:           z.string().trim().max(200).optional().or(z.literal('')),
    customer_type:          z.string().trim().max(100).optional().or(z.literal('')),
    payment_terms:          z.string().trim().max(200).optional().or(z.literal('')),
    default_pickup_notes:   z.string().trim().max(1000).optional().or(z.literal('')),
}).merge(addressSubSchema).merge(billingAddressSubSchema);

// Fields to send on PATCH. Separate from the schema (which is for validation)
// because we need to know which DB columns to send back on edit — clearing
// a field means explicitly sending `null`, not omitting it.
const ADDRESS_FIELDS = [
    'address', 'address_postcode', 'address_latitude', 'address_longitude',
    'address_google_place_id', 'address_source',
];
const BILLING_ADDRESS_FIELDS = [
    'billing_address', 'billing_address_postcode',
    'billing_address_latitude', 'billing_address_longitude',
    'billing_address_google_place_id', 'billing_address_source',
];

const EMPTY_ADDRESS_FIELDS = {
    address: '',
    address_postcode: '',
    address_latitude: null,
    address_longitude: null,
    address_google_place_id: '',
    address_source: null,
};

const EMPTY_BILLING_ADDRESS_FIELDS = {
    billing_address: '',
    billing_address_postcode: '',
    billing_address_latitude: null,
    billing_address_longitude: null,
    billing_address_google_place_id: '',
    billing_address_source: null,
};

export function CustomerFormPage() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAdmin } = useCurrentUser();

    const { data: existing, isLoading: isLoadingExisting, isError, error } = useQuery({
        queryKey: ['customer', id],
        queryFn: () => api.get(`/customers/${id}`),
        enabled: isEdit,
    });

    const {
        register,
        handleSubmit,
        reset,
        setError,
        setValue,
        getValues,
        watch,
        control,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            company_name: '',
            customer_type: '',
            payment_terms: '',
            default_pickup_notes: '',
            ...EMPTY_ADDRESS_FIELDS,
            billing_address_differs: false,
            ...EMPTY_BILLING_ADDRESS_FIELDS,
        },
    });

    // Watch billing_address_differs so the conditional block re-renders
    // when the toggle flips.
    const billingDiffers = watch('billing_address_differs');

    useEffect(() => {
        if (existing) {
            reset({
                first_name: existing.first_name || '',
                last_name: existing.last_name || '',
                email: existing.email || '',
                phone: existing.phone || '',
                company_name: existing.company_name || '',
                customer_type: existing.customer_type || '',
                payment_terms: existing.payment_terms || '',
                default_pickup_notes: existing.default_pickup_notes || '',
                // Primary address
                address: existing.address || '',
                address_postcode: existing.address_postcode || '',
                address_latitude: existing.address_latitude ?? null,
                address_longitude: existing.address_longitude ?? null,
                address_google_place_id: existing.address_google_place_id || '',
                address_source: existing.address_source || null,
                // Billing address
                billing_address_differs: existing.billing_address_differs === true,
                billing_address: existing.billing_address || '',
                billing_address_postcode: existing.billing_address_postcode || '',
                billing_address_latitude: existing.billing_address_latitude ?? null,
                billing_address_longitude: existing.billing_address_longitude ?? null,
                billing_address_google_place_id: existing.billing_address_google_place_id || '',
                billing_address_source: existing.billing_address_source || null,
            });
        }
    }, [existing, reset]);

    const mutation = useMutation({
        mutationFn: async (data) => {
            // Strip empties → null for nullable fields; leave required ones as-is
            const clean = {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                phone: data.phone,
            };
            for (const field of ['company_name', 'customer_type', 'payment_terms', 'default_pickup_notes']) {
                if (data[field] && data[field].trim() !== '') {
                    clean[field] = data[field];
                } else if (isEdit) {
                    clean[field] = null;
                }
            }

            // Primary address fields — same pattern: send value if filled,
            // explicit null on edit if cleared.
            for (const field of ADDRESS_FIELDS) {
                const v = data[field];
                if (field === 'address_latitude' || field === 'address_longitude') {
                    // Numbers: send if it's a finite number, otherwise null on edit.
                    if (typeof v === 'number' && Number.isFinite(v)) {
                        clean[field] = v;
                    } else if (isEdit) {
                        clean[field] = null;
                    }
                } else {
                    if (v && String(v).trim() !== '') {
                        clean[field] = v;
                    } else if (isEdit) {
                        clean[field] = null;
                    }
                }
            }

            // Billing address fields — always send the toggle. The address
            // fields themselves only get sent when toggle is on; if toggle
            // is off (or just flipped off), we explicitly clear them on edit
            // so the DB doesn't keep stale billing data.
            clean.billing_address_differs = data.billing_address_differs === true;

            if (data.billing_address_differs === true) {
                for (const field of BILLING_ADDRESS_FIELDS) {
                    const v = data[field];
                    if (field === 'billing_address_latitude' || field === 'billing_address_longitude') {
                        if (typeof v === 'number' && Number.isFinite(v)) {
                            clean[field] = v;
                        } else if (isEdit) {
                            clean[field] = null;
                        }
                    } else {
                        if (v && String(v).trim() !== '') {
                            clean[field] = v;
                        } else if (isEdit) {
                            clean[field] = null;
                        }
                    }
                }
            } else if (isEdit) {
                // Toggle is off — clear all billing fields explicitly.
                for (const field of BILLING_ADDRESS_FIELDS) {
                    clean[field] = null;
                }
            }

            if (isEdit) {
                return api.patch(`/customers/${id}`, clean);
            }
            return api.post('/customers', clean);
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            if (isEdit) {
                queryClient.invalidateQueries({ queryKey: ['customer', id] });
                toast.success('Customer updated');
                navigate(`/customers/${id}`);
            } else {
                toast.success('Customer created');
                navigate(`/customers/${result.id}`);
            }
        },
        onError: (err) => {
            if (err.fieldErrors) {
                for (const [field, messages] of Object.entries(err.fieldErrors)) {
                    setError(field, { message: Array.isArray(messages) ? messages[0] : messages });
                }
                toast.error('Please fix the errors below');
            } else {
                toast.error(err.message || 'Failed to save');
            }
        },
    });

    // Apply an addressObj (from AddressInput) to a set of form fields
    // matching either the primary or billing prefix.
    function applyAddressToFields(addressObj, prefix /* '' or 'billing_' */) {
        const p = prefix; // 'address' or 'billing_address'
        setValue(`${p}address`,                 addressObj?.address || '',                { shouldDirty: true });
        setValue(`${p}address_postcode`,        addressObj?.postcode || '',               { shouldDirty: true });
        setValue(`${p}address_latitude`,        typeof addressObj?.latitude === 'number'  ? addressObj.latitude  : null, { shouldDirty: true });
        setValue(`${p}address_longitude`,       typeof addressObj?.longitude === 'number' ? addressObj.longitude : null, { shouldDirty: true });
        setValue(`${p}address_google_place_id`, addressObj?.google_place_id || '',        { shouldDirty: true });
        setValue(`${p}address_source`,          addressObj?.address_source || null,       { shouldDirty: true });
    }

    // Copy primary address fields into billing address fields.
    function copyAddressToBilling() {
        const v = getValues();
        setValue('billing_address',                 v.address || '',                 { shouldDirty: true });
        setValue('billing_address_postcode',        v.address_postcode || '',        { shouldDirty: true });
        setValue('billing_address_latitude',        v.address_latitude ?? null,      { shouldDirty: true });
        setValue('billing_address_longitude',       v.address_longitude ?? null,     { shouldDirty: true });
        setValue('billing_address_google_place_id', v.address_google_place_id || '', { shouldDirty: true });
        setValue('billing_address_source',          v.address_source || null,        { shouldDirty: true });
        toast.success('Copied from address');
    }

    if (!isAdmin) {
        return <ErrorPage message="You don't have permission to edit customers." />;
    }

    if (isEdit && isLoadingExisting) return <LoadingPage />;
    if (isEdit && isError) return <ErrorPage message={error?.message || 'Could not load customer'} />;

    const title = isEdit
        ? `Edit ${[existing?.first_name, existing?.last_name].filter(Boolean).join(' ') || 'customer'}`
        : 'New customer';

    // Watch the primary address string for the AddressInput value prop.
    const addressValue = watch('address');
    const billingAddressValue = watch('billing_address');

    return (
        <div className="max-w-2xl">
            <PageHeader
                title={title}
                subtitle={isEdit ? 'Update customer details and preferences.' : 'Add a new customer to the system.'}
                backTo={isEdit ? `/customers/${id}` : '/customers'}
                backLabel={isEdit ? 'Back to customer' : 'Back to customers'}
            />

            <form onSubmit={handleSubmit((data) => mutation.mutate(data))} noValidate>
                <div className="space-y-6">
                    <FormSection title="Identity">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                                label="First name"
                                required
                                {...register('first_name')}
                                error={errors.first_name?.message}
                            />
                            <Input
                                label="Last name"
                                required
                                {...register('last_name')}
                                error={errors.last_name?.message}
                            />
                            <Input
                                label="Email"
                                type="email"
                                required
                                {...register('email')}
                                error={errors.email?.message}
                            />
                            <Input
                                label="Phone"
                                type="tel"
                                required
                                {...register('phone')}
                                error={errors.phone?.message}
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Company & type">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                                label="Company name"
                                {...register('company_name')}
                                error={errors.company_name?.message}
                            />
                            <Input
                                label="Customer type"
                                list="customer-type-suggestions"
                                {...register('customer_type')}
                                error={errors.customer_type?.message}
                                hint="Start typing or pick a suggestion"
                            />
                            <datalist id="customer-type-suggestions">
                                {CUSTOMER_TYPE_SUGGESTIONS.map((t) => (
                                    <option key={t} value={t} />
                                ))}
                            </datalist>
                        </div>
                    </FormSection>

                    <FormSection
                        title="Address"
                        subtitle="Primary address. Used for invoicing unless a separate billing address is set below."
                    >
                        <AddressInput
                            value={addressValue || ''}
                            onChange={(addressObj) => applyAddressToFields(addressObj, '')}
                            placeholder="Search address…"
                        />

                        {/* Toggle for billing address. Default off. */}
                        <div className="mt-4 pt-4 border-t border-paper-border-soft">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    {...register('billing_address_differs')}
                                    className="w-4 h-4"
                                />
                                <span className="text-sm text-ink">
                                    Billing address differs from primary
                                </span>
                            </label>

                            {billingDiffers && (
                                <div className="mt-3 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs uppercase tracking-wide font-medium text-ink-subtle">
                                            Billing address
                                        </span>
                                        <button
                                            type="button"
                                            onClick={copyAddressToBilling}
                                            className="inline-flex items-center gap-1 text-xs text-ink-subtle hover:text-ink"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                            Copy from address
                                        </button>
                                    </div>
                                    <AddressInput
                                        value={billingAddressValue || ''}
                                        onChange={(addressObj) => applyAddressToFields(addressObj, 'billing_')}
                                        placeholder="Search billing address…"
                                    />
                                </div>
                            )}
                        </div>
                    </FormSection>

                    <FormSection
                        title="Billing & preferences"
                        subtitle="Free-text fields. Default pickup notes are not automatically applied — you'll be prompted per booking."
                    >
                        <div className="grid grid-cols-1 gap-4">
                            <Input
                                label="Payment terms"
                                {...register('payment_terms')}
                                error={errors.payment_terms?.message}
                                hint='e.g. "Net 30", "On completion", "Monthly invoice"'
                            />
                            <Input
                                label="Default pickup notes"
                                {...register('default_pickup_notes')}
                                error={errors.default_pickup_notes?.message}
                                hint='e.g. "Meet at hotel concierge desk", "Call on arrival"'
                            />
                        </div>
                    </FormSection>
                </div>

                <div className="mt-8 flex items-center justify-end gap-3 pt-6 border-t border-paper-border-soft">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate(isEdit ? `/customers/${id}` : '/customers')}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" loading={isSubmitting || mutation.isPending} disabled={isEdit && !isDirty}>
                        <Save className="h-4 w-4" />
                        {isEdit ? 'Save changes' : 'Create customer'}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function FormSection({ title, subtitle, children }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">{title}</h2>
                {subtitle && <p className="mt-1 text-xs text-ink-subtle">{subtitle}</p>}
            </div>
            <div className="card-body">{children}</div>
        </div>
    );
}
